import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import type { TooltipProps } from "recharts";
import type { ValueType, NameType } from "recharts/types/component/DefaultTooltipContent";
import { BarChart2, TrendingUp, Package, Users } from "lucide-react";

type Period = "day" | "week" | "month" | "year";

interface ProductStat {
  productId: string;
  name: string;
  totalQuantity: number;
  totalRevenue: number;
}

interface ClientStat {
  clientId: string;
  name: string;
  orderCount: number;
  totalSpend: number;
}

interface StatisticsData {
  ordersOverTime: { label: string; count: number; revenue: number }[];
  topProductsByQuantity: ProductStat[];
  topProductsByRevenue: ProductStat[];
  topClientsByOrderCount: ClientStat[];
  topClientsBySpend: ClientStat[];
}

const periodLabels: Record<Period, string> = {
  day: "Dag",
  week: "Vecka",
  month: "Månad",
  year: "År",
};

const periodDescriptions: Record<Period, string> = {
  day: "idag",
  week: "denna vecka",
  month: "denna månad",
  year: "detta år",
};

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("sv-SE", {
    style: "currency",
    currency: "SEK",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function CustomTooltip({ active, payload, label }: TooltipProps<ValueType, NameType>) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-lg border bg-background p-3 shadow-sm text-sm space-y-1">
      <p className="font-medium">{label}</p>
      {payload.map((entry) => (
        <p key={entry.dataKey as string} style={{ color: entry.color }}>
          {entry.name === "Ordrar"
            ? `${entry.value} ordrar`
            : formatCurrency(Number(entry.value))}
        </p>
      ))}
    </div>
  );
}

export default function AdminStatistics() {
  const [period, setPeriod] = useState<Period>("month");
  const [chartType, setChartType] = useState<"bar" | "line">("bar");
  const [productSort, setProductSort] = useState<"quantity" | "revenue">("quantity");
  const [clientSort, setClientSort] = useState<"orders" | "spend">("orders");

  const { data, isLoading } = useQuery<StatisticsData>({
    queryKey: ["/api/admin/statistics", period],
    queryFn: async () => {
      const res = await fetch(`/api/admin/statistics?period=${period}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch statistics");
      return res.json();
    },
  });

  const totalOrders = data?.ordersOverTime.reduce((acc, d) => acc + d.count, 0) ?? 0;
  const totalRevenue = data?.ordersOverTime.reduce((acc, d) => acc + d.revenue, 0) ?? 0;

  const displayedProducts =
    productSort === "quantity"
      ? (data?.topProductsByQuantity ?? [])
      : (data?.topProductsByRevenue ?? []);

  const displayedClients =
    clientSort === "orders"
      ? (data?.topClientsByOrderCount ?? [])
      : (data?.topClientsBySpend ?? []);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Statistik</h1>
          <p className="text-muted-foreground">Översikt av affärsprestation</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {(["day", "week", "month", "year"] as Period[]).map((p) => (
            <Button
              key={p}
              variant={period === p ? "default" : "outline"}
              size="sm"
              onClick={() => setPeriod(p)}
              data-testid={`button-period-${p}`}
            >
              {periodLabels[p]}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <BarChart2 className="h-5 w-5 text-primary" />
              </div>
              <div>
                {isLoading ? (
                  <Skeleton className="h-8 w-16" />
                ) : (
                  <p className="text-2xl font-bold" data-testid="stat-total-orders">{totalOrders}</p>
                )}
                <p className="text-sm text-muted-foreground">Ordrar {periodDescriptions[period]}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <div>
                {isLoading ? (
                  <Skeleton className="h-8 w-24" />
                ) : (
                  <p className="text-2xl font-bold" data-testid="stat-total-revenue">{formatCurrency(totalRevenue)}</p>
                )}
                <p className="text-sm text-muted-foreground">Omsättning {periodDescriptions[period]}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <CardTitle>Ordrar &amp; Omsättning</CardTitle>
              <CardDescription>
                Ordrar och intäkter {periodDescriptions[period]}
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant={chartType === "bar" ? "default" : "outline"}
                size="sm"
                onClick={() => setChartType("bar")}
                data-testid="button-chart-bar"
              >
                Stapel
              </Button>
              <Button
                variant={chartType === "line" ? "default" : "outline"}
                size="sm"
                onClick={() => setChartType("line")}
                data-testid="button-chart-line"
              >
                Linje
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : !data?.ordersOverTime.length ? (
            <div className="h-64 flex items-center justify-center text-muted-foreground text-sm">
              Ingen data för vald period
            </div>
          ) : chartType === "bar" ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.ordersOverTime}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} tickFormatter={(v: number) => `${v} kr`} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar yAxisId="left" dataKey="count" name="Ordrar" fill="hsl(var(--primary))" radius={[3, 3, 0, 0]} />
                <Bar yAxisId="right" dataKey="revenue" name="Omsättning" fill="hsl(142.1 76.2% 36.3%)" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={data.ordersOverTime}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} tickFormatter={(v: number) => `${v} kr`} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line yAxisId="left" type="monotone" dataKey="count" name="Ordrar" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                <Line yAxisId="right" type="monotone" dataKey="revenue" name="Omsättning" stroke="hsl(142.1 76.2% 36.3%)" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-base">Topprodukter</CardTitle>
              </div>
              <div className="flex gap-2">
                <Button
                  variant={productSort === "quantity" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setProductSort("quantity")}
                  data-testid="button-product-sort-quantity"
                >
                  Antal
                </Button>
                <Button
                  variant={productSort === "revenue" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setProductSort("revenue")}
                  data-testid="button-product-sort-revenue"
                >
                  Omsättning
                </Button>
              </div>
            </div>
            <CardDescription>
              Produkter {periodDescriptions[period]}, sorterade efter {productSort === "quantity" ? "sålt antal" : "omsättning"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
              </div>
            ) : displayedProducts.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">Ingen data för vald period</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8">#</TableHead>
                    <TableHead>Produkt</TableHead>
                    <TableHead className="text-right">Antal</TableHead>
                    <TableHead className="text-right">Omsättning</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayedProducts.map((p, idx) => (
                    <TableRow key={p.productId} data-testid={`row-product-${p.productId}`}>
                      <TableCell className="text-muted-foreground font-mono text-sm">{idx + 1}</TableCell>
                      <TableCell className="font-medium">{p.name}</TableCell>
                      <TableCell className="text-right">{p.totalQuantity} st</TableCell>
                      <TableCell className="text-right">{formatCurrency(p.totalRevenue)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-base">Toppkunder</CardTitle>
              </div>
              <div className="flex gap-2">
                <Button
                  variant={clientSort === "orders" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setClientSort("orders")}
                  data-testid="button-client-sort-orders"
                >
                  Ordrar
                </Button>
                <Button
                  variant={clientSort === "spend" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setClientSort("spend")}
                  data-testid="button-client-sort-spend"
                >
                  Köpbelopp
                </Button>
              </div>
            </div>
            <CardDescription>
              Kunder {periodDescriptions[period]}, sorterade efter {clientSort === "orders" ? "antal ordrar" : "köpbelopp"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
              </div>
            ) : displayedClients.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">Ingen data för vald period</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8">#</TableHead>
                    <TableHead>Kund</TableHead>
                    <TableHead className="text-right">Ordrar</TableHead>
                    <TableHead className="text-right">Köpbelopp</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayedClients.map((c, idx) => (
                    <TableRow key={c.clientId} data-testid={`row-client-${c.clientId}`}>
                      <TableCell className="text-muted-foreground font-mono text-sm">{idx + 1}</TableCell>
                      <TableCell className="font-medium">{c.name}</TableCell>
                      <TableCell className="text-right">{c.orderCount}</TableCell>
                      <TableCell className="text-right">{formatCurrency(c.totalSpend)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
